import React, { useState } from 'react';
import { Upload } from 'lucide-react';
import { uploadBook } from './firebaseService';
import { uploadFileToFirestore } from './chunked-firestore-storage';
import { checkDuplicateBookURL } from './seedDemoData';
import { BookCountrySelector } from './CountrySystem';
import { isUserGated } from './SubscriptionSystem';

const PAGE_TITLES = [
  'Book Details',
  'Files & Settings'
];

const TOTAL_STEPS = PAGE_TITLES.length;

const UploadBookForm = ({
  currentUser,
  books, // 👈 MAKE SURE THIS IS PASSED FROM APP.JS
  onUploadSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);

  const [bookForm, setBookForm] = useState({
    title: '',
    author: '',
    genre: '',
    description: '',
    pageCount: '',
    amazonLink: '',
    coverImage: '',
    bookFileName: '',
    language: 'English',
    isExplicit: false,
    keywords: '',
    mainCharacters: '',
    targetCountries: [],
    readerTypes: {
      standard: false,
      kindleUnlimited: false,
      verifiedPurchase: false
    }
  });

  const [coverImagePreview, setCoverImagePreview] = useState('');
  const [bookFileObject, setBookFileObject] = useState(null);

  const handleCoverImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
      if (!validTypes.includes(file.type)) {
        alert('Please upload a valid image file (.jpg, .png, or .gif)');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setCoverImagePreview(reader.result);
        setBookForm(prev => ({ ...prev, coverImage: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleBookFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['application/pdf', 'application/epub+zip'];
      if (!validTypes.includes(file.type)) {
        alert('Please upload a valid file (.pdf or .epub)');
        return;
      }

      const maxSize = 50 * 1024 * 1024;
      if (file.size > maxSize) {
        alert('File is too large. Maximum size is 50MB.');
        return;
      }

      setBookFileObject(file);
      setBookForm(prev => ({
        ...prev,
        bookFileName: file.name
      }));

      const sizeMB = (file.size / 1024 / 1024).toFixed(2);
      alert(`File "${file.name}" ready to upload (${sizeMB}MB)`);
    }
  };

  // ============================================
  // PAGE 1 VALIDATION (blocks "Next" until satisfied)
  // Same per-field checks/messages as before, just grouped.
  // ============================================
  const validatePage1 = () => {
    if (!bookForm.title.trim()) {
      alert('Please enter your book title.');
      return false;
    }
    if (!bookForm.author.trim()) {
      alert('Please enter the author name.');
      return false;
    }
    if (!bookForm.amazonLink.trim()) {
      alert(' Please provide a valid Amazon link.');
      return false;
    }
    if (!bookForm.genre) {
      alert('Please select a genre.');
      return false;
    }
    if (!bookForm.language) {
      alert('Please select a language.');
      return false;
    }
    if (!bookForm.pageCount || parseInt(bookForm.pageCount, 10) < 1) {
      alert('Please enter the number of pages.');
      return false;
    }
    // Explicit content is a yes/no checkbox — any value is valid.
    if (!bookForm.description.trim()) {
      alert('Please write a book summary.');
      return false;
    }
    // Keywords and Main Character are optional — never block.
    return true;
  };

  // ============================================
  // PAGE 2 VALIDATION (blocks "Submit" until satisfied)
  // Same per-field checks/messages as before, just grouped.
  // ============================================
  const validatePage2 = () => {
    if (!bookForm.coverImage) {
      alert('Please upload a front cover image.');
      return false;
    }
    const hasReaderType = bookForm.readerTypes.standard ||
      bookForm.readerTypes.kindleUnlimited ||
      bookForm.readerTypes.verifiedPurchase;
    if (!hasReaderType) {
      alert('Please select at least one reader type (Standard, Kindle Unlimited, or Verified Purchase)');
      return false;
    }
    if (bookForm.readerTypes.standard && !bookFileObject) {
      alert('Standard reviews require a PDF or ePub file. Please upload your book file.');
      return false;
    }
    // Target marketplace is optional — an empty selection means "All Amazon Marketplaces".
    return true;
  };

  const goNext = () => {
    if (!validatePage1()) return;
    setCurrentStep(prev => Math.min(prev + 1, TOTAL_STEPS - 1));
  };

  const goBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const handleSubmitClick = () => {
    if (!validatePage2()) return;
    handleBookUpload();
  };

 const handleBookUpload = async () => {
  setLoading(true);

  // ============================================
  // STEP 1: BASIC VALIDATION
  // ============================================
  if (
    !bookForm.title ||
    !bookForm.author ||
    !bookForm.genre ||
    !bookForm.pageCount ||
    !bookForm.description ||
    !bookForm.amazonLink
  )

  {
    setLoading(false);
    alert('Please fill in all required fields');
    return;
  }

  // ============================================
  // STEP 2: DUPLICATE AMAZON LINK CHECK
  // ============================================

  const normalizeLink = (link) => {
    if (!link) return '';
    return link.trim().toLowerCase().replace(/\/+$/, '').replace(/[?#].*$/, '');
  };

  const currentLink = normalizeLink(bookForm.amazonLink);

  if (!currentLink) {
    setLoading(false);
    alert(' Please provide a valid Amazon link.');
    return;
  }

  if (!books || !Array.isArray(books)) {
    setLoading(false);
    alert(' No books data available.');
    return;
  }

  const duplicate = books.find(b => {
    if (!b || !b.amazonLink) return false;
    const normalizedBookLink = normalizeLink(b.amazonLink);
    return normalizedBookLink === currentLink;
  });

  if (duplicate) {
    setLoading(false);
    alert(
      ` THIS AMAZON LINK ALREADY UPLOADED!\n\n` +
      `Book: "${duplicate.title}"\n` +
      `Author: ${duplicate.author}\n\n` +
      `Each link can only be used ONCE!`
    );
    return;
  }

  // ============================================
  // STEP 3: DUPLICATE AUTHOR CHECK
  // (Two different accounts cannot upload the same author name)
  // ============================================

  const currentAuthor = (bookForm.author || '').toLowerCase().trim();

  if (currentAuthor) {
    const existingBookByAuthor = books.find(b => {
      if (!b || !b.author || typeof b.author !== 'string') {
        return false;
      }

      const existingAuthor = b.author.toLowerCase().trim();
      return existingAuthor === currentAuthor;
    });

    if (existingBookByAuthor) {
      const isOwnAuthor = existingBookByAuthor.uploadedBy === currentUser.id;

      if (!isOwnAuthor) {
        setLoading(false);
        alert(
          ` THIS AUTHOR NAME IS ALREADY IN USE!\n\n` +
          `Author: "${bookForm.author}"\n` +
          `Book already on platform: "${existingBookByAuthor.title || 'Unknown'}"\n\n` +
          `REASON:\n` +
          `Each author can only be represented by ONE account to prevent confusion.\n\n` +
          `SOLUTIONS:\n` +
          `1. If this is YOUR author name, log in to your original account\n` +
          `2. Use "Forgot Password" to recover your account\n` +
          `3. Contact support if you believe this is an error\n\n` +
          `This ensures authors maintain control over their brand.`
        );
        return;
      } else {
        const confirmed = window.confirm(
          ` UPLOADING ANOTHER BOOK BY THIS AUTHOR\n\n` +
          `You previously uploaded:\n` +
          `"${existingBookByAuthor.title || 'Unknown'}" by ${bookForm.author}\n\n` +
          `Now uploading:\n` +
          `"${bookForm.title}" by ${bookForm.author}\n\n` +
          `Continue?`
        );

        if (!confirmed) {
          setLoading(false);
          return;
        }
      }
    }
  }

  // ============================================
  // STEP 4: READER TYPE CHECK
  // ============================================

  const hasReaderType = bookForm.readerTypes.standard ||
    bookForm.readerTypes.kindleUnlimited ||
    bookForm.readerTypes.verifiedPurchase;

  if (!hasReaderType) {
    setLoading(false);
    alert('Please select at least one reader type (Standard, Kindle Unlimited, or Verified Purchase)');
    return;
  }

  if (bookForm.readerTypes.standard && !bookFileObject) {
    setLoading(false);
    alert('Standard reviews require a PDF or ePub file. Please upload your book file.');
    return;
  }

  if (!currentUser || typeof currentUser.coins !== 'number') {
    setLoading(false);
    alert('User data not fully loaded. Please refresh and try again.');
    return;
  }

  // ============================================
  // STEP 5: DUPLICATE CHECK VIA API
  // ============================================

  const duplicateCheck = await checkDuplicateBookURL(bookForm.amazonLink);
  if (duplicateCheck.isDuplicate) {
    setLoading(false);
    alert(duplicateCheck.message);
    return;
  }

  // ============================================
  // STEP 6: UPLOAD LOGIC
  // ============================================

  try {
    setUploadProgress(10);

    const fullSummary = bookForm.description.trim();
    const words = fullSummary.split(/\s+/).filter(w => w);
    const shortDescription = words.slice(0, 10).join(' ') + (words.length > 10 ? '...' : '');

    const result = await uploadBook({
      title: bookForm.title,
      author: bookForm.author,
      genre: bookForm.genre,
      description: shortDescription,
      fullSummary,
      pageCount: parseInt(bookForm.pageCount, 10),
      amazonLink: bookForm.amazonLink,
      completedReviews: 0,
      coverImage: bookForm.coverImage,
      bookFileName: bookForm.bookFileName,
      hasBookFile: !!bookFileObject,
      language: bookForm.language,
      isExplicit: bookForm.isExplicit,
      readerTypes: bookForm.readerTypes,
      keywords: bookForm.keywords,
      requestedReviewsCompleted: 0,
      mainCharacters: bookForm.mainCharacters,
      targetCountries: bookForm.targetCountries || [],
      visibilityEndDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'pending',
      approval: {
        status: 'pending',
        reviewedBy: null,
        reviewedAt: null,
        adminNotes: ''
      },
      authorGated: isUserGated(currentUser)
    }, currentUser.id);

    if (!result.success) {
      throw new Error(result.error);
    }

    setUploadProgress(30);

    if (bookFileObject && result.bookId) {
      setUploadingFile(true);
      await uploadFileToFirestore(bookFileObject, result.bookId);
      setUploadProgress(90);
    }

    setUploadProgress(100);
    setLoading(false);
    setUploadingFile(false);

    setBookForm({
      title: '',
      author: '',
      genre: '',
      description: '',
      pageCount: '',
      amazonLink: '',
      coverImage: '',
      bookFileName: '',
      language: 'English',
      isExplicit: false,
      keywords: '',
      mainCharacters: '',
      targetCountries: [],
      readerTypes: {
        standard: false,
        kindleUnlimited: false,
        verifiedPurchase: false
      }
    });
    setCoverImagePreview('');
    setBookFileObject(null);
    setUploadProgress(0);
    setCurrentStep(0);

    alert('✅ Book submitted for approval!\n\nYou will be notified once approved (usually within 24 hours).');

    onUploadSuccess();
  } catch (error) {
    setLoading(false);
    setUploadingFile(false);
    setUploadProgress(0);
    console.error('Upload error:', error);
    alert('Failed to upload book: ' + error.message);
  }
};

  const wordCount = bookForm.description.trim().split(/\s+/).filter(w => w).length;

  const renderPage1 = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-bold">Book Details</h3>

      <div>
        <label className="block text-sm font-medium mb-1">Book Title *</label>
        <input
          value={bookForm.title}
          onChange={(e) => setBookForm(prev => ({ ...prev, title: e.target.value }))}
          className="w-full px-3 py-2 border rounded"
          placeholder="Enter your book title"
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Author *</label>
        <input
          value={bookForm.author}
          onChange={(e) => setBookForm(prev => ({ ...prev, author: e.target.value }))}
          className="w-full px-3 py-2 border rounded"
          placeholder="Author name"
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Amazon URL *</label>
        <input
          value={bookForm.amazonLink}
          onChange={(e) => setBookForm(prev => ({ ...prev, amazonLink: e.target.value }))}
          className="w-full px-3 py-2 border rounded"
          placeholder="https://amazon.com/dp/YOUR_BOOK_ID"
        />
        <p className="text-xs text-gray-500 mt-1">
          Example: https://www.amazon.com/Meditations-Marcus-Aurelius-ebook/dp/B08RYB957M/
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Genre *</label>
        <select
          value={bookForm.genre}
          onChange={(e) => setBookForm(prev => ({ ...prev, genre: e.target.value }))}
          className="w-full px-3 py-2 border rounded"
        >
          <option value="">Select genre</option>
          <option>Fiction</option>
          <option>Non-Fiction</option>
          <option>Romance</option>
          <option>Mystery, Thriller & Suspense</option>
          <option>Science Fiction</option>
          <option>Fantasy</option>
          <option>Horror</option>
          <option>Historical</option>
          <option>Young Adult</option>
          <option>Children's Books</option>
          <option>Biography & Memoir</option>
          <option>Self-Help</option>
          <option>Business & Money</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Language *</label>
        <select
          value={bookForm.language}
          onChange={(e) => setBookForm(prev => ({ ...prev, language: e.target.value }))}
          className="w-full px-3 py-2 border rounded"
        >
          <option>English</option>
          <option>Spanish</option>
          <option>French</option>
          <option>German</option>
          <option>Dutch</option>
          <option>Italian</option>
          <option>Japanese</option>
          <option>Other</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Page Count *</label>
        <input
          type="number"
          value={bookForm.pageCount}
          onChange={(e) => setBookForm(prev => ({ ...prev, pageCount: e.target.value }))}
          className="w-full px-3 py-2 border rounded"
          min="1"
          placeholder="Number of pages"
        />
      </div>

      <div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={bookForm.isExplicit}
            onChange={(e) => setBookForm(prev => ({ ...prev, isExplicit: e.target.checked }))}
            className="w-5 h-5"
          />
          <span className="text-sm font-medium">Explicit content?</span>
        </label>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Book Summary *</label>
        <textarea
          value={bookForm.description}
          onChange={(e) => setBookForm(prev => ({ ...prev, description: e.target.value }))}
          rows="6"
          className="w-full px-3 py-2 border rounded"
          placeholder="Write a detailed summary of your book. The first 10 words will be shown on the browse page, and the full summary will appear on the book details page."
        />
        <p className="text-xs text-gray-500 mt-1">
          Word count: {wordCount} words
          {wordCount > 0 && (
            <span className="text-blue-600 ml-2">
              ✓ First 10 words will show on browse page
            </span>
          )}
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Keywords (optional)</label>
        <input
          value={bookForm.keywords}
          onChange={(e) => setBookForm(prev => ({ ...prev, keywords: e.target.value }))}
          className="w-full px-3 py-2 border rounded"
          placeholder="e.g., adventure, mystery, romance"
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Main Character (optional)</label>
        <textarea
          value={bookForm.mainCharacters}
          onChange={(e) => setBookForm(prev => ({ ...prev, mainCharacters: e.target.value }))}
          rows="2"
          className="w-full px-3 py-2 border rounded"
          placeholder="Brief description of the main character(s)"
        />
      </div>
    </div>
  );

  const renderPage2 = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-bold">Files & Settings</h3>

      <div>
        <label className="block text-sm font-medium mb-2">
          📸 Front Cover *
        </label>
        <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-blue-400 transition">
          <input
            type="file"
            accept=".jpg,.jpeg,.png,.gif"
            onChange={handleCoverImageChange}
            className="hidden"
            id="coverImageInput"
          />
          <label htmlFor="coverImageInput" className="cursor-pointer">
            {coverImagePreview ? (
              <div>
                <img src={coverImagePreview} alt="Cover preview" className="max-h-48 mx-auto mb-3 rounded" />
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setCoverImagePreview('');
                    setBookForm(prev => ({ ...prev, coverImage: '' }));
                  }}
                  className="text-red-600 text-sm underline"
                >
                  Remove File
                </button>
              </div>
            ) : (
              <>
                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-blue-600 font-medium mb-1">Drop files here to upload</p>
                <p className="text-sm text-gray-500">Accepted file types: .jpg, .png, .gif</p>
              </>
            )}
          </label>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Please provide the 2D image of your front cover only.
        </p>
        <div className="mt-2 bg-red-50 border border-red-200 rounded-lg p-2">
          <p className="text-xs text-red-700 font-semibold">
            ⚠️ Your cover must exactly match your Amazon listing.
            Books with incorrect covers will be rejected.
          </p>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">
           Upload Book File (PDF or ePub) {bookForm.readerTypes.standard ? '*' : ''}
        </label>
        {!bookForm.readerTypes.standard && (
          <p className="text-sm text-gray-500 mb-3">
            Not required for your selected reader type(s) — you can skip this step.
          </p>
        )}
        <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-blue-400 transition">
          <input
            type="file"
            accept=".pdf,.epub"
            onChange={handleBookFileChange}
            className="hidden"
            id="bookFileInput"
          />
          <label htmlFor="bookFileInput" className="cursor-pointer">
            <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-blue-600 font-medium mb-1">Drop files here to upload</p>
            <p className="text-sm text-gray-500">Accepted file types: .epub, .pdf (Max 50MB)</p>

            {bookForm.bookFileName && (
              <p className="mt-3 text-sm text-green-600 font-medium">
                ✅ {bookForm.bookFileName}
              </p>
            )}
          </label>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Your book will be securely stored and only accessible to approved reviewers.
        </p>
      </div>

      <div>
        <h3 className="text-lg font-bold mb-3">Choose your readers</h3>
        <p className="text-sm text-gray-600 mb-4">
          Choose one or more of the options below. No matter which options you select,
          all reviews are posted directly to Amazon by your readers.
        </p>

        <div className="space-y-3">
          {/* Standard Option */}
          <label className={`flex items-start gap-3 p-4 border-2 rounded-xl cursor-pointer transition ${
            bookForm.readerTypes.standard ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'
          }`}>
            <input
              type="checkbox"
              checked={bookForm.readerTypes.standard}
              onChange={(e) => setBookForm(prev => ({
                ...prev,
                readerTypes: { ...prev.readerTypes, standard: e.target.checked }
              }))}
              className="w-5 h-5 mt-1"
            />
            <div className="flex-1">
              <div className="font-semibold text-blue-600 mb-1">Standard</div>
              <p className="text-sm text-gray-700 mb-2">
                All readers can review your book using a free copy that you provide to read.
              </p>
              <p className="text-sm text-blue-600 font-medium">
                Requires a PDF or ePub copy of your book
              </p>
            </div>
          </label>

          {/* Kindle Unlimited Option */}
          <label className={`flex items-start gap-3 p-4 border-2 rounded-xl cursor-pointer transition ${
            bookForm.readerTypes.kindleUnlimited ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-orange-300'
          }`}>
            <input
              type="checkbox"
              checked={bookForm.readerTypes.kindleUnlimited}
              onChange={(e) => setBookForm(prev => ({
                ...prev,
                readerTypes: { ...prev.readerTypes, kindleUnlimited: e.target.checked }
              }))}
              className="w-5 h-5 mt-1"
            />
            <div className="flex-1">
              <div className="font-semibold text-orange-600 mb-1">Kindle Unlimited</div>
              <p className="text-sm text-gray-700 mb-2">
                Readers with a Kindle Unlimited subscription can review your book.
                Your readers will read your book using Kindle Unlimited.
              </p>
              <p className="text-sm text-orange-600 font-medium">
                Requires your book to be free for KU subscribers
              </p>
            </div>
          </label>

          {/* Verified Purchase Option */}
          <label className={`flex items-start gap-3 p-4 border-2 rounded-xl cursor-pointer transition ${
            bookForm.readerTypes.verifiedPurchase ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-green-300'
          }`}>
            <input
              type="checkbox"
              checked={bookForm.readerTypes.verifiedPurchase}
              onChange={(e) => setBookForm(prev => ({
                ...prev,
                readerTypes: { ...prev.readerTypes, verifiedPurchase: e.target.checked }
              }))}
              className="w-5 h-5 mt-1"
            />
            <div className="flex-1">
              <div className="font-semibold text-green-600 mb-1">Verified Purchases</div>
              <p className="text-sm text-gray-700 mb-2">
                Readers who agree to purchase your book on Amazon can review your book.
                Your readers will read your book using Kindle.
              </p>
            </div>
          </label>
        </div>
      </div>

      <BookCountrySelector
        selectedCountries={bookForm.targetCountries}
        onChange={(countries) => setBookForm(prev => ({
          ...prev,
          targetCountries: countries
        }))}
      />
    </div>
  );

  const isLastStep = currentStep === TOTAL_STEPS - 1;
  const progressPct = Math.round(((currentStep + 1) / TOTAL_STEPS) * 100);

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-2xl font-bold">Upload Your Book</h2>
        <div className="text-sm text-gray-600">
          <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 rounded-full">
            New Book
          </span>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow p-6">
        {/* Progress indicator */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              Step {currentStep + 1} of {TOTAL_STEPS}: {PAGE_TITLES[currentStep]}
            </span>
            <span className="text-sm text-gray-500">{progressPct}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>

        <div className="space-y-6">
          {currentStep === 0 ? renderPage1() : renderPage2()}

          {/* Upload progress (final step only, while submitting) */}
          {isLastStep && (loading || uploadingFile) && uploadProgress > 0 && (
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-blue-900">Upload Progress</span>
                <span className="text-sm font-bold text-blue-900">{uploadProgress}%</span>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}

          {/* Wizard navigation */}
          <div className="flex items-center justify-between gap-3 pt-2">
            <button
              type="button"
              onClick={goBack}
              disabled={currentStep === 0 || loading || uploadingFile}
              className="px-5 py-3 rounded-lg border-2 border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Back
            </button>

            {isLastStep ? (
              <button
                type="button"
                onClick={handleSubmitClick}
                disabled={loading || uploadingFile}
                className="flex-1 bg-indigo-600 text-white py-3 rounded-lg hover:bg-indigo-700 disabled:bg-indigo-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-lg font-semibold"
              >
                {uploadingFile ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Uploading Book File...</span>
                  </>
                ) : loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Submitting for Approval...</span>
                  </>
                ) : (
                  'SUBMIT FOR APPROVAL'
                )}
              </button>
            ) : (
              <button
                type="button"
                onClick={goNext}
                className="flex-1 bg-indigo-600 text-white py-3 rounded-lg hover:bg-indigo-700 font-semibold"
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadBookForm;
